import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../../lib/config'
import { logEffectiveChatModelFromResponse } from '../../lib/logEffectiveChatModel'
import { useAuth } from '../../components/auth/AuthProvider'
import { parseTimestamp } from '../../components/lib/dateUtils'
import Sidebar, { type ChatHistoryItem } from '../components/Sidebar'
import TopBar from '../components/TopBar'
import WelcomeScreen from '../components/WelcomeScreen'
import MessageList, { type RefMessage, type RefFile } from '../components/MessageList'
import InputBar from '../components/InputBar'
import FTUModal from '../components/FTUModal'
import GuideModal from '../components/GuideModal'
import TCModal from '../components/TCModal'
import ProfileModal from '../components/ProfileModal'
import RenameModal from '../components/RenameModal'
import ConfirmModal from '../components/ConfirmModal'
import FloatingAudioControl from '../components/FloatingAudioControl'
import { Toaster } from 'sonner'
import { shouldAutoRevokeSkill } from '../lib/skillLifecycle'
import { FALLBACK_SKILLS } from '../lib/skills'
import { skillOfferMessage } from '../components/skills/SkillOfferCard'

const FTU_STORAGE_KEY = 'athena-ftu-done'

/** Limits payload from GET /chat/sessions (last N messages per chat). Omit param only if you need full history on first load. */
const CHAT_SESSIONS_MAX_MESSAGES_PER_CHAT = 400
/** Page size for sidebar session list (GET /chat/sessions?limit=&offset=). */
const CHAT_SESSIONS_PAGE_SIZE = 30

interface Message {
  id: string
  content: string
  sender: 'user' | 'aira'
  timestamp: Date
  file_path?: string
  file_name?: string
  file_size?: string
  /** Set when the provider stopped at max output tokens (SSE assistant_truncated). */
  truncatedMaxTokens?: boolean
  /** Inline skill intent offer (client-only; not persisted as assistant content). */
  skillOffer?: { skillId: string; skillTitle: string; resolved?: boolean }
}

interface ChatHistory {
  id: string
  title: string
  messages: Message[]
  timestamp: Date
  created_at?: string
  /** Active invocable skill for this session (cleared when skill completes). */
  activeSkillId?: string
  /** Assistant replies produced while the current skill was active. */
  skillAssistantTurns?: number
}

function sortMessagesChronological(a: Message, b: Message): number {
  const dt = a.timestamp.getTime() - b.timestamp.getTime()
  if (dt !== 0) return dt
  return a.id.localeCompare(b.id)
}

type ChatStreamPieceResult =
  | { kind: 'text'; text: string }
  | { kind: 'title_update'; chat_id: string; title: string }
  | { kind: 'assistant_truncated' }
  | { kind: 'control' }

/** Backend frames each event as `data: …--|\\n\\n`; chunks from fetch may split mid-frame. */
const CHAT_SSE_DELIM = '--|\n\n'

function drainChatSseDataPieces(buffer: string, incoming: string): { rest: string; pieces: string[] } {
  let buf = buffer + incoming
  const pieces: string[] = []
  for (;;) {
    const i = buf.indexOf(CHAT_SSE_DELIM)
    if (i === -1) return { rest: buf, pieces }
    const raw = buf.slice(0, i)
    buf = buf.slice(i + CHAT_SSE_DELIM.length)
    const line = raw.trimStart()
    if (line.startsWith('data: ')) {
      pieces.push(line.slice(6))
    }
  }
}

function trailingChatSseDataPiece(buffer: string): string | null {
  const line = buffer.trimStart()
  if (line.startsWith('data: ')) return line.slice(6)
  return null
}

/** Parse one SSE payload after the `data: ` prefix (JSON events vs plain text chunks). */
function parseChatStreamSsePiece(piece: string): ChatStreamPieceResult {
  try {
    const parsed = JSON.parse(piece) as {
      type?: string
      chat_id?: string
      title?: string
    }
    if (parsed.type === 'title_update' && parsed.chat_id && parsed.title) {
      return { kind: 'title_update', chat_id: String(parsed.chat_id), title: String(parsed.title) }
    }
    if (parsed.type === 'assistant_truncated') {
      return { kind: 'assistant_truncated' }
    }
    if (parsed.type === 'error') {
      return { kind: 'control' }
    }
    return { kind: 'control' }
  } catch {
    return { kind: 'text', text: piece }
  }
}

type ApiHistoryRow = {
  id: string
  role: string
  content: string
  timestamp?: string
  file_path?: string
  file_name?: string
  file_size?: string
}

function mapApiHistoryRowToMessage(h: ApiHistoryRow): Message {
  return {
    id: h.id,
    content: String(h.content ?? ''),
    sender: h.role === 'user' ? 'user' : 'aira',
    timestamp: h.timestamp ? parseTimestamp(h.timestamp) : new Date(),
    file_path: h.file_path,
    file_name: h.file_name,
    file_size: h.file_size,
  }
}

function normalizeChatContentForCompare(s: string): string {
  return s.replace(/\r\n/g, '\n').trimEnd()
}

/** True when server-stored text is the same reply we streamed (allow minor newline/trim differences). */
function assistantContentMatchesStream(
  stored: string,
  streamed: string
): boolean {
  const a = normalizeChatContentForCompare(stored)
  const b = normalizeChatContentForCompare(streamed)
  if (a === b) return true
  if (!a || !b) return false
  const prefixLen = Math.min(a.length, b.length, 65536)
  if (prefixLen > 0 && a.slice(0, prefixLen) === b.slice(0, prefixLen)) {
    return Math.abs(a.length - b.length) <= 4
  }
  return false
}

/**
 * Preserve truncatedMaxTokens after GET stream-history (API has no flag).
 * Tags the last assistant whose content matches the streamed body, or the last assistant in the list.
 */
function mergeTruncationIntoLastAssistant(
  messages: Message[],
  lastAssistantContent: string,
  hadTruncation: boolean
): Message[] {
  if (!hadTruncation || messages.length === 0) return messages
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].sender !== 'aira') continue
    if (assistantContentMatchesStream(messages[i].content, lastAssistantContent)) {
      return messages.map((m, j) =>
        j === i ? { ...m, truncatedMaxTokens: true } : m
      )
    }
  }
  const tail = messages[messages.length - 1]
  if (tail.sender === 'aira') {
    return messages.map((m, j) =>
      j === messages.length - 1 ? { ...m, truncatedMaxTokens: true } : m
    )
  }
  return messages
}

/**
 * If history GET ran before the assistant row was committed, the list may end with the user message.
 * Re-append the streamed assistant so Continue and content stay correct.
 */
function mergeHistoryWithTruncatedAssistantIfPending(
  mapped: Message[],
  streamedAssistant: Message,
  hadTruncation: boolean
): Message[] {
  if (!hadTruncation || mapped.length === 0) return mapped
  const last = mapped[mapped.length - 1]
  if (last.sender === 'user') {
    return [...mapped, { ...streamedAssistant }]
  }
  return mapped
}

type ApiSessionRow = {
  id: string | number
  name: string
  history: Array<{
    id: string
    role: 'user' | 'assistant'
    content: string
    timestamp?: string
    file_path?: string
    file_name?: string
    file_size?: string
  }>
  created_at?: string
}

function mapApiSessionsToChatHistories(sessions: ApiSessionRow[]): ChatHistory[] {
  return sessions.map((s) => ({
    id: String(s.id),
    title: s.name || 'New Chat',
    messages: hydrateSkillUiMessages(
      (s.history || []).map((h) => ({
        id: h.id,
        content: String(h.content ?? ''),
        sender: h.role === 'user' ? ('user' as const) : ('aira' as const),
        timestamp: h.timestamp ? parseTimestamp(h.timestamp) : new Date(),
        file_path: h.file_path,
        file_name: h.file_name,
        file_size: h.file_size,
      }))
    ),
    timestamp: s.created_at ? new Date(s.created_at + 'Z') : new Date(),
    created_at: s.created_at,
  }))
}

type ChatMode = 'ochat' | 'cchat'

interface ChatAppProps {
  chatMode?: ChatMode
}

function messageToRefMessage(m: Message): RefMessage {
    if (m.sender === 'user') {
    const files: RefFile[] = []
    if (m.file_name) {
      const ext = m.file_name.split('.').pop()?.toLowerCase() ?? ''
      files.push({
        name: m.file_name,
        ext,
        messageId: m.file_path ? m.id : undefined,
      })
    }
    return { role: 'user', id: m.id, text: m.content, files, timestamp: m.timestamp }
  }
  return {
    role: 'ai',
    id: m.id,
    data: {
      h: 'Athena',
      b: m.content,
      pts: [],
      a: '',
      tags: [],
      truncatedMaxTokens: m.truncatedMaxTokens,
    },
    timestamp: m.timestamp,
    ...(m.skillOffer
      ? {
          skillOffer: {
            skillId: m.skillOffer.skillId,
            skillTitle: m.skillOffer.skillTitle,
            active: !m.skillOffer.resolved,
          },
        }
      : {}),
  }
}

const SKILL_CHOICE_ACCURATE = 'Get accurate answers'
const SKILL_CHOICE_QUICK = 'Just a quick take'

const skillAccurateInvoke = (skillTitle: string) => `Run ${skillTitle} now for my business.`
const skillQuickInvoke = (skillTitle: string) =>
  `Give me a quick take on ${skillTitle} for my business`

const isSkillChoiceLabel = (content: string) =>
  content === SKILL_CHOICE_ACCURATE || content === SKILL_CHOICE_QUICK

type SkillInvokeMatch = {
  skillId: string
  skillTitle: string
  choice: typeof SKILL_CHOICE_ACCURATE | typeof SKILL_CHOICE_QUICK
}

const matchSkillInvokeMessage = (content: string): SkillInvokeMatch | null => {
  for (const skill of FALLBACK_SKILLS) {
    if (content === skillAccurateInvoke(skill.title)) {
      return {
        skillId: skill.id,
        skillTitle: skill.title,
        choice: SKILL_CHOICE_ACCURATE,
      }
    }
    if (content === skillQuickInvoke(skill.title)) {
      return {
        skillId: skill.id,
        skillTitle: skill.title,
        choice: SKILL_CHOICE_QUICK,
      }
    }
  }
  return null
}

/**
 * Server only stores the API invoke text (e.g. "Run Role Clarity now…").
 * Rebuild the live UI thread: skill title → offer → choice label → replies.
 * Safe after full page refresh when local offer state is gone.
 */
function hydrateSkillUiMessages(msgs: Message[]): Message[] {
  const out: Message[] = []
  for (const m of msgs) {
    if (m.sender === 'user') {
      const matched = matchSkillInvokeMessage(m.content)
      if (matched) {
        const { skillId, skillTitle, choice } = matched
        const alreadyThreaded = out.some(
          (x) => x.skillOffer?.skillId === skillId || (x.sender === 'user' && x.content === skillTitle)
        )
        const t = m.timestamp?.getTime() ?? Date.now()
        if (!alreadyThreaded) {
          out.push({
            id: `skill-starter-${m.id}`,
            content: skillTitle,
            sender: 'user',
            timestamp: new Date(t - 2000),
          })
          out.push({
            id: `skill-offer-${m.id}`,
            content: skillOfferMessage(skillTitle),
            sender: 'aira',
            timestamp: new Date(t - 1000),
            skillOffer: { skillId, skillTitle, resolved: true },
          })
        }
        out.push({ ...m, content: choice })
        continue
      }
    }
    out.push(m)
  }
  return out
}

/**
 * Re-attach client-only skill offers after stream-history reload (same tab).
 * Target order: skill title → offer → choice → Athena reply.
 */
function mergeSkillOffersIntoHistory(serverMsgs: Message[], localMsgs: Message[]): Message[] {
  // Always normalize persisted invoke strings first (covers refresh + mid-stream reload)
  let result = hydrateSkillUiMessages(serverMsgs.map((m) => ({ ...m })))

  const offers = localMsgs.filter((m) => m.skillOffer)
  if (!offers.length) {
    const base = result[0]?.timestamp?.getTime() ?? Date.now()
    return result.map((m, i) => ({ ...m, timestamp: new Date(base + i * 1000) }))
  }

  for (const offer of offers) {
    const skillTitle = offer.skillOffer?.skillTitle
    const skillId = offer.skillOffer?.skillId
    if (!skillTitle || !skillId) continue

    if (result.some((m) => m.skillOffer?.skillId === skillId)) {
      // Keep live resolved flag from local when still on the same offer instance
      result = result.map((m) =>
        m.skillOffer?.skillId === skillId
          ? {
              ...m,
              skillOffer: {
                ...m.skillOffer!,
                resolved: offer.skillOffer?.resolved ?? m.skillOffer!.resolved,
              },
            }
          : m
      )
      continue
    }

    const localIdx = localMsgs.findIndex((m) => m.id === offer.id)
    const localStarter = localMsgs
      .slice(0, localIdx)
      .reverse()
      .find((m) => m.sender === 'user' && m.content === skillTitle)
    const localChoice = localMsgs
      .slice(localIdx + 1)
      .find((m) => m.sender === 'user' && isSkillChoiceLabel(m.content))

    let choiceIdx = -1
    let titleIdx = -1
    for (let i = 0; i < result.length; i++) {
      if (result[i].sender !== 'user') continue
      if (isSkillChoiceLabel(result[i].content)) {
        choiceIdx = i
        break
      }
      if (result[i].content === skillTitle && titleIdx < 0) titleIdx = i
    }

    const offerMsg: Message = {
      ...offer,
      skillOffer: { ...offer.skillOffer!, resolved: true },
    }

    if (choiceIdx >= 0) {
      const prefix: Message[] = []
      const hasStarter = result
        .slice(0, choiceIdx)
        .some((m) => m.sender === 'user' && m.content === skillTitle)
      if (localStarter && !hasStarter) prefix.push({ ...localStarter })
      prefix.push(offerMsg)
      result = [...result.slice(0, choiceIdx), ...prefix, ...result.slice(choiceIdx)]
    } else if (titleIdx >= 0) {
      const toInsert: Message[] = [offerMsg]
      if (localChoice && !result.some((m) => isSkillChoiceLabel(m.content))) {
        toInsert.push({ ...localChoice })
      }
      result = [...result.slice(0, titleIdx + 1), ...toInsert, ...result.slice(titleIdx + 1)]
    } else {
      const toInsert: Message[] = []
      if (localStarter) toInsert.push({ ...localStarter })
      toInsert.push(offerMsg)
      if (localChoice) toInsert.push({ ...localChoice })
      result = [...result, ...toInsert]
    }
  }

  const base = result[0]?.timestamp?.getTime() ?? Date.now()
  return result.map((m, i) => ({ ...m, timestamp: new Date(base + i * 1000) }))
}

export default function ChatApp({ chatMode = 'ochat' }: ChatAppProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAdmin } = useAuth()

  const [currentChatId, setCurrentChatId] = useState<string | null>(null)
  const [chatHistories, setChatHistories] = useState<ChatHistory[]>([])
  const chatHistoriesRef = useRef(chatHistories)
  useEffect(() => {
    chatHistoriesRef.current = chatHistories
  }, [chatHistories])
  /** Server-side total session count; used with sessionsNextOffset for sidebar pagination */
  const [sessionsTotal, setSessionsTotal] = useState<number | null>(null)
  const [sessionsNextOffset, setSessionsNextOffset] = useState(0)
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false)
  const [showFTU, setShowFTU] = useState(() => typeof localStorage !== 'undefined' && localStorage.getItem(FTU_STORAGE_KEY) !== 'true')
  const [showGuide, setShowGuide] = useState(false)
  const [showTC, setShowTC] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('athena-sidebar-open')
      if (stored !== null) return stored === 'true'
    }
    return true
  })

  useEffect(() => {
    localStorage.setItem('athena-sidebar-open', String(sidebarOpen))
  }, [sidebarOpen])
  const [isTyping, setIsTyping] = useState(false)
  const [errorType, setErrorType] = useState<'offline' | 'ai' | null>(null)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [chatToRename, setChatToRename] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [chatToDelete, setChatToDelete] = useState<string | null>(null)
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null)
  const [isEditingMessage, setIsEditingMessage] = useState(false)

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth <= 1024
    return false
  })

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1024px)')
    setIsMobile(mql.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const scrollRef = useRef<HTMLDivElement>(null)
  const sessionsLoadedRef = useRef(false)
  /** When set, landing flow will send a first message — do not auto-select an existing session */
  const pendingLandingPromptRef = useRef(false)
  const chatCreationInProgressRef = useRef(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const mediaSourceRef = useRef<MediaSource | null>(null)
  const audioCache = useRef<Record<string, Blob>>({})
  const [pendingInitialPrompt, setPendingInitialPrompt] = useState<string | null>(null)
  const initialPromptProcessedRef = useRef(false)

  const currentChat = chatHistories.find((c) => c.id === currentChatId)
  const messages = useMemo(() => {
    const msgs = currentChat?.messages ?? []
    // Skill-offer threads use intentional insert order; don't re-sort by timestamp
    if (msgs.some((m) => m.skillOffer)) return msgs
    return [...msgs].sort(sortMessagesChronological)
  }, [currentChat?.messages])

  const userDisplayName = user?.displayName ?? user?.username ?? 'User'
  const refMessages: RefMessage[] = useMemo(
    () => messages.map((m) => messageToRefMessage(m)),
    [messages]
  )

  const userInitials = userDisplayName
    ? userDisplayName.trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U'
    : 'U'
  const userFirstName = userDisplayName.trim().split(/\s+/)[0] || 'there'
  const chatTitle = currentChat?.title ?? 'New conversation'
  const hasHistory = chatHistories.length > 0

  const SCROLL_NEAR_BOTTOM_THRESHOLD = 120
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceFromBottom > SCROLL_NEAR_BOTTOM_THRESHOLD) return
    el.scrollTop = el.scrollHeight
  }, [messages, scrollToBottom])

  const createNewChat = useCallback(async (): Promise<string> => {
    try {
      const res = await fetch(`${API_BASE_URL}create-initial-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // name: `Chat ${new Date().toLocaleString('en-GB', { timeZone: 'Asia/Kolkata' })}`,
          name: `New Chat`,
          chat_type: chatMode,
        }),
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to create chat')
      const { id, name } = await res.json()
      const newChat: ChatHistory = {
        id: String(id),
        title: name || 'New Chat',
        messages: [],
        timestamp: new Date(),
      }
      setChatHistories((prev) => [newChat, ...prev])
      setCurrentChatId(String(id))
      setSessionsTotal((t) => (t != null ? t + 1 : t))
      return String(id)
    } catch {
      const fallbackId = `chat_${Date.now()}`
      const newChat: ChatHistory = {
        id: fallbackId,
        title: 'New Chat',
        messages: [],
        timestamp: new Date(),
      }
      setChatHistories((prev) => [newChat, ...prev])
      setCurrentChatId(fallbackId)
      return fallbackId
    }
  }, [chatMode])

  useEffect(() => {
    const state = location.state as { initialPrompt?: string } | undefined
    const initialPrompt = state?.initialPrompt
    if (initialPrompt && !initialPromptProcessedRef.current) {
      initialPromptProcessedRef.current = true
      pendingLandingPromptRef.current = true
      setPendingInitialPrompt(initialPrompt)
      navigate(location.pathname || '/ochat', { replace: true })
    }
  }, [location.state, location.pathname, navigate])

  useEffect(() => {
    if (sessionsLoadedRef.current) return
    sessionsLoadedRef.current = true
    const loadSessions = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}chat/sessions?max_messages_per_session=${CHAT_SESSIONS_MAX_MESSAGES_PER_CHAT}&limit=${CHAT_SESSIONS_PAGE_SIZE}&offset=0`,
          { credentials: 'include' }
        )
        if (!res.ok) return
        const data: ApiSessionRow[] | { items: ApiSessionRow[]; total: number } = await res.json()
        const rows: ApiSessionRow[] = Array.isArray(data) ? data : data.items
        const total: number = Array.isArray(data) ? data.length : data.total
        setSessionsTotal(total)
        setSessionsNextOffset(rows.length)
        if (rows.length > 0) {
          const mapped = mapApiSessionsToChatHistories(rows)
          setChatHistories(mapped)
          if (!pendingLandingPromptRef.current) setCurrentChatId(mapped[0].id)
        }
        // No sessions: stay on welcome / draft — do not create a server session until the user sends a message
      } catch {
        // Failed to load sessions; leave currentChatId null if no history
      }
    }
    loadSessions()
  }, [pendingInitialPrompt])

  const loadMoreSessions = useCallback(async () => {
    if (loadingMoreSessions || sessionsTotal == null || sessionsNextOffset >= sessionsTotal) return
    setLoadingMoreSessions(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}chat/sessions?max_messages_per_session=${CHAT_SESSIONS_MAX_MESSAGES_PER_CHAT}&limit=${CHAT_SESSIONS_PAGE_SIZE}&offset=${sessionsNextOffset}`,
        { credentials: 'include' }
      )
      if (!res.ok) return
      const data: { items: ApiSessionRow[]; total: number } = await res.json()
      const rows = data.items ?? []
      setSessionsTotal(data.total)
      if (rows.length === 0) {
        setSessionsNextOffset(data.total)
        return
      }
      setSessionsNextOffset((prev) => prev + rows.length)
      const mapped = mapApiSessionsToChatHistories(rows)
      setChatHistories((prev) => {
        const seen = new Set(prev.map((c) => c.id))
        const merged = [...prev]
        for (const c of mapped) {
          if (!seen.has(c.id)) {
            seen.add(c.id)
            merged.push(c)
          }
        }
        return merged
      })
    } catch {
      // ignore
    } finally {
      setLoadingMoreSessions(false)
    }
  }, [loadingMoreSessions, sessionsTotal, sessionsNextOffset])

  const hasMoreSessions = sessionsTotal != null && sessionsNextOffset < sessionsTotal

  const sendMessage = useCallback(
    async (
      content: string,
      files?: RefFile[],
      fileToUpload?: File | null,
      skillId?: string,
      opts?: { skipUserMessage?: boolean; displayContent?: string }
    ) => {
      if (errorType === 'offline') return
      let chatIdToUse = currentChatId
      if (!chatIdToUse && !chatCreationInProgressRef.current) {
        chatCreationInProgressRef.current = true
        try {
          chatIdToUse = await createNewChat()
          if (!chatIdToUse) return
          setCurrentChatId(chatIdToUse)
        } finally {
          chatCreationInProgressRef.current = false
        }
      }
      const existingChat = chatHistories.find((c) => c.id === chatIdToUse)
      const resolvedSkillId =
        skillId ??
        existingChat?.activeSkillId
      const uiContent = opts?.displayContent?.trim() || content

      if (skillId) {
        setChatHistories((prev) =>
          prev.map((chat) =>
            chat.id === chatIdToUse
              ? { ...chat, activeSkillId: skillId, skillAssistantTurns: 0 }
              : chat
          )
        )
      }

      if (!opts?.skipUserMessage) {
        const userMessage: Message = {
          id: Date.now().toString(),
          content: uiContent,
          sender: 'user',
          timestamp: new Date(),
        }
        if (files?.length && files[0]) {
          userMessage.file_name = files[0].name
          userMessage.file_path = ''
        }
        setChatHistories((prev) =>
          prev.map((chat) => {
            if (chat.id !== chatIdToUse) return chat
            const updated = [...chat.messages, userMessage]
            const title =
              chat.title === 'New Chat' && updated.length === 2
                ? content.split(' ').slice(0, 4).join(' ').slice(0, 30) || 'New Chat'
                : chat.title
            return {
              ...chat,
              messages: updated,
              title,
              timestamp: new Date(),
              ...(skillId ? { activeSkillId: skillId, skillAssistantTurns: 0 } : {}),
            }
          })
        )
      } else if (skillId) {
        setChatHistories((prev) =>
          prev.map((chat) =>
            chat.id === chatIdToUse
              ? { ...chat, activeSkillId: skillId, skillAssistantTurns: 0, timestamp: new Date() }
              : chat
          )
        )
      }
      setTimeout(() => scrollToBottom(), 0)
      setIsTyping(true)
      setErrorType(null)
      try {
        const chatToUse = chatHistoriesRef.current.find((c) => c.id === chatIdToUse) ?? existingChat
        const messagesToSend = chatToUse?.messages ?? []
        const cleaned = messagesToSend.filter((m) => !m.skillOffer)
        let historyMsgs = cleaned
        if (opts?.skipUserMessage || opts?.displayContent) {
          // Don't double-send skill-title / choice bubbles as history + current message
          const skillStarters = new Set<string>()
          for (let i = 0; i < messagesToSend.length; i++) {
            if (!messagesToSend[i].skillOffer) continue
            for (let j = i - 1; j >= 0; j--) {
              if (messagesToSend[j].sender === 'user') {
                skillStarters.add(messagesToSend[j].content)
                break
              }
            }
          }
          historyMsgs = cleaned.filter((m) => {
            if (m.sender !== 'user') return true
            if (m.content === content || m.content === opts?.displayContent) return false
            if (skillStarters.has(m.content)) return false
            return true
          })
        }
        const formData = new FormData()
        // API gets the real invoke text (e.g. skill title); UI may show displayContent
        formData.append('message', content)
        formData.append(
          'history',
          JSON.stringify(
            historyMsgs.map((m) => ({
              role: m.sender === 'user' ? 'user' : 'assistant',
              content: m.content,
            }))
          )
        )
        formData.append('chat_id', chatIdToUse!)
        formData.append('chat_type', chatMode)
        if (resolvedSkillId) formData.append('skill_id', resolvedSkillId)
        if (fileToUpload) formData.append('file', fileToUpload)
        const res = await fetch(`${API_BASE_URL}chat/stream`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        })
        logEffectiveChatModelFromResponse(res, 'send')
        if (!res.body) throw new Error('No response body')
        const reader = res.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let botResp = ''
        let assistantTruncatedMaxTokens = false
        let sseBuffer = ''
        const processSsePiece = (piece: string) => {
          if (!piece) return
          const parsed = parseChatStreamSsePiece(piece)
          if (parsed.kind === 'title_update') {
            setChatHistories((prev) =>
              prev.map((c) => (c.id === parsed.chat_id ? { ...c, title: parsed.title } : c))
            )
            return
          }
          if (parsed.kind === 'assistant_truncated') {
            assistantTruncatedMaxTokens = true
            return
          }
          if (parsed.kind === 'control') return
          botResp += parsed.text
          const partial: Message = {
            id: 'streaming',
            content: botResp,
            sender: 'aira',
            timestamp: new Date(),
          }
          setChatHistories((prev) =>
            prev.map((c) => {
              if (c.id !== chatIdToUse) return c
              const without = c.messages.filter((m) => m.id !== 'streaming')
              return { ...c, messages: [...without, partial] }
            })
          )
        }
        while (true) {
          const { value, done } = await reader.read()
          const chunk = value ? decoder.decode(value, { stream: !done }) : ''
          const drained = drainChatSseDataPieces(sseBuffer, chunk)
          sseBuffer = drained.rest
          for (const piece of drained.pieces) {
            processSsePiece(piece)
          }
          if (done) {
            const tail = trailingChatSseDataPiece(sseBuffer)
            if (tail) processSsePiece(tail)
            break
          }
        }
        // Replace streaming placeholder with final message (existing logic continues below)
        const finalContent = botResp
        const final: Message = {
          id: (Date.now() + 1).toString(),
          content: finalContent,
          sender: 'aira',
          timestamp: new Date(),
          ...(assistantTruncatedMaxTokens ? { truncatedMaxTokens: true } : {}),
        }
        setChatHistories((prev) =>
          prev.map((c) => {
            if (c.id !== chatIdToUse) return c
            const without = c.messages.filter((m) => m.id !== 'streaming')
            const nextTurns = resolvedSkillId
              ? (c.skillAssistantTurns ?? 0) + 1
              : c.skillAssistantTurns
            const revoke =
              !!resolvedSkillId &&
              shouldAutoRevokeSkill(finalContent, nextTurns ?? 1, resolvedSkillId, {
                truncated: assistantTruncatedMaxTokens,
              })
            return {
              ...c,
              messages: [...without, final],
              timestamp: new Date(),
              skillAssistantTurns: revoke ? 0 : nextTurns,
              activeSkillId: revoke ? undefined : c.activeSkillId,
            }
          })
        )
        try {
          if (assistantTruncatedMaxTokens) {
            await new Promise((r) => setTimeout(r, 150))
          }
          const historyRes = await fetch(`${API_BASE_URL}chat/stream-history/${chatIdToUse}`, {
            credentials: 'include',
          })
          if (historyRes.ok) {
            const historyData = await historyRes.json()
            const history: ApiHistoryRow[] = historyData.history || []
            setChatHistories((prev) =>
              prev.map((c) => {
                if (c.id !== chatIdToUse) return c
                let mapped = history.map(mapApiHistoryRowToMessage)
                if (assistantTruncatedMaxTokens) {
                  mapped = mergeHistoryWithTruncatedAssistantIfPending(mapped, final, assistantTruncatedMaxTokens)
                }
                mapped = mergeTruncationIntoLastAssistant(
                  mapped,
                  finalContent,
                  assistantTruncatedMaxTokens
                )
                // Keep the client-only skill offer card in the thread
                mapped = mergeSkillOffersIntoHistory(mapped, c.messages)
                return {
                  ...c,
                  messages: mapped,
                  timestamp: new Date(),
                }
              })
            )
          }
        } catch {
          // ignore
        }
      } catch {
        setErrorType('ai')
      } finally {
        setIsTyping(false)
      }
    },
    [currentChatId, chatHistories, createNewChat, chatMode, errorType, scrollToBottom]
  )

  useEffect(() => {
    if (!pendingInitialPrompt) return
    const prompt = pendingInitialPrompt
    setPendingInitialPrompt(null)
    queueMicrotask(() => {
      void sendMessage(prompt)
    })
  }, [pendingInitialPrompt, sendMessage])

  const startSkillOffer = useCallback(
    async (skillTitle: string, skillId: string) => {
      if (errorType === 'offline') return
      let chatIdToUse = currentChatId
      if (!chatIdToUse && !chatCreationInProgressRef.current) {
        chatCreationInProgressRef.current = true
        try {
          chatIdToUse = await createNewChat()
          if (!chatIdToUse) return
          setCurrentChatId(chatIdToUse)
        } finally {
          chatCreationInProgressRef.current = false
        }
      }
      const userMessage: Message = {
        id: Date.now().toString(),
        content: skillTitle,
        sender: 'user',
        timestamp: new Date(),
      }
      setChatHistories((prev) =>
        prev.map((chat) => {
          if (chat.id !== chatIdToUse) return chat
          const updated = [...chat.messages, userMessage]
          const title =
            chat.title === 'New Chat'
              ? skillTitle.slice(0, 30) || 'New Chat'
              : chat.title
          return {
            ...chat,
            messages: updated,
            title,
            timestamp: new Date(),
            activeSkillId: skillId,
            skillAssistantTurns: 0,
          }
        })
      )
      setTimeout(() => scrollToBottom(), 0)
      setIsTyping(true)
      setErrorType(null)
      void fetch(`${API_BASE_URL}chat/prefetch-skill`, {
        method: 'POST',
        credentials: 'include',
        body: (() => {
          const fd = new FormData()
          fd.append('skill_id', skillId)
          return fd
        })(),
      }).catch(() => {
        /* prefetch is best-effort */
      })
      window.setTimeout(() => {
        const offer: Message = {
          id: (Date.now() + 1).toString(),
          content: skillOfferMessage(skillTitle),
          sender: 'aira',
          timestamp: new Date(),
          skillOffer: { skillId, skillTitle },
        }
        setChatHistories((prev) =>
          prev.map((chat) =>
            chat.id === chatIdToUse
              ? { ...chat, messages: [...chat.messages, offer], timestamp: new Date() }
              : chat
          )
        )
        setIsTyping(false)
        setTimeout(() => scrollToBottom(), 0)
      }, 900)
    },
    [currentChatId, createNewChat, errorType, scrollToBottom]
  )

  const markOfferResolved = useCallback((offerMessageId: string) => {
    setChatHistories((prev) =>
      prev.map((chat) => ({
        ...chat,
        messages: chat.messages.map((m) =>
          m.id === offerMessageId && m.skillOffer
            ? { ...m, skillOffer: { ...m.skillOffer, resolved: true } }
            : m
        ),
      }))
    )
  }, [])

  const onSkillOfferAccurate = useCallback(
    (offerMessageId: string, skillId: string) => {
      const chat = chatHistoriesRef.current.find((c) => c.id === currentChatId)
      const offer = chat?.messages.find((m) => m.id === offerMessageId)
      const skillTitle = offer?.skillOffer?.skillTitle
      if (!skillTitle) return
      markOfferResolved(offerMessageId)
      // UI shows the button choice; API gets an explicit invoke + skill_id so overlay/RAG run
      void sendMessage(skillAccurateInvoke(skillTitle), undefined, undefined, skillId, {
        displayContent: SKILL_CHOICE_ACCURATE,
      })
    },
    [currentChatId, markOfferResolved, sendMessage]
  )

  const onSkillOfferQuick = useCallback(
    (offerMessageId: string, skillId: string) => {
      const chat = chatHistoriesRef.current.find((c) => c.id === currentChatId)
      const offer = chat?.messages.find((m) => m.id === offerMessageId)
      const skillTitle = offer?.skillOffer?.skillTitle
      markOfferResolved(offerMessageId)
      void sendMessage(
        skillTitle ? skillQuickInvoke(skillTitle) : SKILL_CHOICE_QUICK,
        undefined,
        undefined,
        skillId,
        { displayContent: SKILL_CHOICE_QUICK }
      )
    },
    [currentChatId, markOfferResolved, sendMessage]
  )

  useEffect(() => {
    const handleOnline = () => setErrorType(null)
    const handleOffline = () => setErrorType('offline')
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    if (!navigator.onLine) setErrorType('offline')
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleLoadChat = (id: string | number) => {
    if (id === 0 || id === '0') {
      const first = chatHistories[0]
      if (first) {
        setCurrentChatId(first.id)
      }
      if (isMobile) setSidebarOpen(false)
      return
    }
    setCurrentChatId(String(id))
    if (isMobile) setSidebarOpen(false)
  }

  const onFTUClose = () => {
    if (typeof localStorage !== 'undefined') localStorage.setItem(FTU_STORAGE_KEY, 'true')
    setShowFTU(false)
  }

  const handleRenameChat = (chatId: string) => {
    setChatToRename(chatId)
    setRenameDialogOpen(true)
  }

  const confirmRenameChat = useCallback(
    async (newTitle: string) => {
      if (!chatToRename) return
      try {
        const res = await fetch(`${API_BASE_URL}chat/sessions/${chatToRename}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newTitle }),
          credentials: 'include',
        })
        if (!res.ok) throw new Error('rename failed')
        setChatHistories((prev) =>
          prev.map((c) => (c.id === chatToRename ? { ...c, title: newTitle } : c))
        )
      } catch {
        // ignore; could add inline error state
      }
      setRenameDialogOpen(false)
      setChatToRename(null)
    },
    [chatToRename]
  )

  const handleDeleteChat = (chatId: string) => {
    setChatToDelete(chatId)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteChat = useCallback(async () => {
    if (!chatToDelete) return
    try {
      await fetch(`${API_BASE_URL}chat/sessions/${chatToDelete}`, {
        method: 'DELETE',
        credentials: 'include',
      })
    } catch {
      // ignore
    }
    const filtered = chatHistories.filter((c) => c.id !== chatToDelete)
    setChatHistories(filtered)
    setSessionsTotal((t) => (t != null ? Math.max(0, t - 1) : t))
    if (currentChatId === chatToDelete) {
      if (filtered.length > 0) setCurrentChatId(filtered[0].id)
      else setCurrentChatId(null)
    }
    setDeleteDialogOpen(false)
    setChatToDelete(null)
  }, [chatToDelete, currentChatId, chatHistories])

  const stopPlayback = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    if (mediaSourceRef.current) {
      if (mediaSourceRef.current.readyState === 'open') {
        try {
          mediaSourceRef.current.endOfStream()
        } catch {
          // ignore
        }
      }
      mediaSourceRef.current = null
    }
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    setPlayingMessageId(null)
    setIsPaused(false)
  }, [])

  const handleNewChat = useCallback(() => {
    setCurrentChatId(null)
    stopPlayback()
    if (isMobile) setSidebarOpen(false)
  }, [isMobile, stopPlayback])

  const togglePlayPause = useCallback(() => {
    if (!audioRef.current) return
    if (audioRef.current.paused) {
      audioRef.current.play()
      setIsPaused(false)
    } else {
      audioRef.current.pause()
      setIsPaused(true)
    }
  }, [])

  const playMessage = useCallback(
    async (messageId: string, content: string) => {
      if (playingMessageId === messageId && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause()
        setIsPaused(true)
        return
      }
      if (playingMessageId === messageId && audioRef.current?.paused && isPaused) {
        audioRef.current.play()
        setIsPaused(false)
        return
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
        audioRef.current = null
      }
      setPlayingMessageId(messageId)
      setIsPaused(false)

      const cached = audioCache.current[messageId]
      if (cached) {
        const audio = new Audio()
        audio.playbackRate = 1.2
        audioRef.current = audio
        const url = URL.createObjectURL(cached)
        audio.src = url
        audio.onended = () => {
          setPlayingMessageId(null)
          setIsPaused(false)
          audioRef.current = null
          URL.revokeObjectURL(url)
        }
        audio.onerror = () => {
          setPlayingMessageId(null)
          setIsPaused(false)
          audioRef.current = null
          URL.revokeObjectURL(url)
        }
        await audio.play().catch(() => {})
        return
      }

      const abort = new AbortController()
      abortControllerRef.current = abort
      try {
        const res = await fetch(`${API_BASE_URL}tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: content }),
          credentials: 'include',
          signal: abort.signal,
        })
        if (!res.ok) throw new Error('TTS failed')
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No body')

        const allChunks: BlobPart[] = []
        const MIN_BUFFER_SIZE = 32768

        if (!('MediaSource' in window)) {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            if (value) allChunks.push(value)
          }
          abortControllerRef.current = null
          const blob = new Blob(allChunks, { type: 'audio/mpeg' })
          audioCache.current[messageId] = blob
          const audio = new Audio()
          audio.playbackRate = 1.2
          audioRef.current = audio
          const url = URL.createObjectURL(blob)
          audio.src = url
          audio.onended = () => {
            setPlayingMessageId(null)
            setIsPaused(false)
            audioRef.current = null
            URL.revokeObjectURL(url)
          }
          audio.onerror = () => {
            setPlayingMessageId(null)
            setIsPaused(false)
            audioRef.current = null
            URL.revokeObjectURL(url)
          }
          await audio.play().catch(() => {})
          return
        }

        const mediaSource = new MediaSource()
        mediaSourceRef.current = mediaSource
        const mediaUrl = URL.createObjectURL(mediaSource)
        const audio = new Audio()
        audio.src = mediaUrl
        audio.preload = 'auto'
        audio.playbackRate = 1.2
        audioRef.current = audio
        audio.onended = () => {
          setPlayingMessageId(null)
          setIsPaused(false)
          audioRef.current = null
          mediaSourceRef.current = null
          URL.revokeObjectURL(mediaUrl)
        }
        audio.onerror = () => {
          setPlayingMessageId(null)
          setIsPaused(false)
          audioRef.current = null
          mediaSourceRef.current = null
          URL.revokeObjectURL(mediaUrl)
        }

        await new Promise<void>((resolve) => {
          mediaSource.addEventListener('sourceopen', () => resolve(), { once: true })
        })

        let sourceBuffer: SourceBuffer
        try {
          sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg')
        } catch {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            if (value) allChunks.push(value)
          }
          abortControllerRef.current = null
          const blob = new Blob(allChunks, { type: 'audio/mpeg' })
          audioCache.current[messageId] = blob
          const fallbackUrl = URL.createObjectURL(blob)
          audio.src = fallbackUrl
          audio.onended = () => {
            setPlayingMessageId(null)
            setIsPaused(false)
            audioRef.current = null
            URL.revokeObjectURL(fallbackUrl)
          }
          audio.onerror = () => {
            setPlayingMessageId(null)
            setIsPaused(false)
            audioRef.current = null
            URL.revokeObjectURL(fallbackUrl)
          }
          await audio.play().catch(() => {})
          return
        }

        let isPlaying = false
        let bufferedSize = 0

        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read()
              if (done) {
                if (sourceBuffer.updating) {
                  await new Promise<void>((resolve) => {
                    sourceBuffer.addEventListener('updateend', () => resolve(), { once: true })
                  })
                }
                if (mediaSource.readyState === 'open') {
                  mediaSource.endOfStream()
                }
                const blob = new Blob(allChunks, { type: 'audio/mpeg' })
                audioCache.current[messageId] = blob
                break
              }
              if (value) {
                allChunks.push(value)
                bufferedSize += value.length
                if (sourceBuffer.updating) {
                  await new Promise<void>((resolve) => {
                    sourceBuffer.addEventListener('updateend', () => resolve(), { once: true })
                  })
                }
                try {
                  sourceBuffer.appendBuffer(value)
                } catch {
                  break
                }
                if (!isPlaying && bufferedSize >= MIN_BUFFER_SIZE) {
                  isPlaying = true
                  await audio.play().catch(() => {})
                }
              }
            }
          } catch (e) {
            if (e instanceof Error && e.name !== 'AbortError') {
              // stream error
            }
          } finally {
            abortControllerRef.current = null
          }
        }
        processStream()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        setPlayingMessageId(null)
        setIsPaused(false)
        audioRef.current = null
      }
    },
    [playingMessageId, isPaused]
  )

  const regenerateMessage = useCallback(
    async (messageId: string) => {
      if (!currentChatId) return
      const newMessageId = Date.now().toString()
      setRegeneratingMessageId(newMessageId)
      try {
        setChatHistories((prev) =>
          prev.map((c) => {
            if (c.id !== currentChatId) return c
            return {
              ...c,
              messages: [
                ...c.messages.filter((m) => m.id !== messageId),
                {
                  id: newMessageId,
                  content: '',
                  sender: 'aira' as const,
                  timestamp: new Date(),
                },
              ],
            }
          })
        )
        const formData = new FormData()
        formData.append('chat_id', currentChatId)
        const activeSkill = chatHistories.find((c) => c.id === currentChatId)?.activeSkillId
        if (activeSkill) formData.append('skill_id', activeSkill)
        const res = await fetch(`${API_BASE_URL}chat/regenerate`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        })
        logEffectiveChatModelFromResponse(res, 'regenerate')
        if (!res.ok) throw new Error('Regenerate failed')
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No body')
        const decoder = new TextDecoder('utf-8')
        let newContent = ''
        let regenTruncatedMaxTokens = false
        let sseBuffer = ''
        const processRegenPiece = (piece: string) => {
          if (!piece) return
          const parsed = parseChatStreamSsePiece(piece)
          if (parsed.kind === 'title_update') {
            setChatHistories((prev) =>
              prev.map((c) => (c.id === parsed.chat_id ? { ...c, title: parsed.title } : c))
            )
            return
          }
          if (parsed.kind === 'assistant_truncated') {
            regenTruncatedMaxTokens = true
            return
          }
          if (parsed.kind === 'control') return
          newContent += parsed.text
          setChatHistories((prev) =>
            prev.map((c) => {
              if (c.id !== currentChatId) return c
              return {
                ...c,
                messages: c.messages.map((m) =>
                  m.id === newMessageId ? { ...m, content: newContent } : m
                ),
              }
            })
          )
        }
        while (true) {
          const { value, done } = await reader.read()
          const chunk = value ? decoder.decode(value, { stream: !done }) : ''
          const drained = drainChatSseDataPieces(sseBuffer, chunk)
          sseBuffer = drained.rest
          for (const piece of drained.pieces) {
            processRegenPiece(piece)
          }
          if (done) {
            const tail = trailingChatSseDataPiece(sseBuffer)
            if (tail) processRegenPiece(tail)
            break
          }
        }
        const finalRegenContent = newContent
        const regenFinalMessage: Message = {
          id: newMessageId,
          content: finalRegenContent,
          sender: 'aira',
          timestamp: new Date(),
          ...(regenTruncatedMaxTokens ? { truncatedMaxTokens: true } : {}),
        }
        setChatHistories((prev) =>
          prev.map((c) => {
            if (c.id !== currentChatId) return c
            return {
              ...c,
              messages: c.messages.map((m) =>
                m.id === newMessageId
                  ? {
                      ...m,
                      content: finalRegenContent,
                      ...(regenTruncatedMaxTokens ? { truncatedMaxTokens: true } : {}),
                    }
                  : m
              ),
            }
          })
        )
        if (regenTruncatedMaxTokens) {
          await new Promise((r) => setTimeout(r, 150))
        }
        const historyRes = await fetch(`${API_BASE_URL}chat/stream-history/${currentChatId}`, {
          credentials: 'include',
        })
        if (historyRes.ok) {
          const historyData = await historyRes.json()
          const history: ApiHistoryRow[] = historyData.history || []
          setChatHistories((prev) =>
            prev.map((c) => {
              if (c.id !== currentChatId) return c
              let mapped = history.map(mapApiHistoryRowToMessage)
              if (regenTruncatedMaxTokens) {
                mapped = mergeHistoryWithTruncatedAssistantIfPending(
                  mapped,
                  regenFinalMessage,
                  regenTruncatedMaxTokens
                )
              }
              mapped = mergeTruncationIntoLastAssistant(
                mapped,
                finalRegenContent,
                regenTruncatedMaxTokens
              )
              mapped = mergeSkillOffersIntoHistory(mapped, c.messages)
              return {
                ...c,
                messages: mapped,
                timestamp: new Date(),
              }
            })
          )
        }
      } catch {
        setErrorType('ai')
        setChatHistories((prev) =>
          prev.map((c) => {
            if (c.id !== currentChatId) return c
            return {
              ...c,
              messages: c.messages.filter((m) => m.id !== newMessageId),
            }
          })
        )
      } finally {
        setRegeneratingMessageId(null)
      }
    },
    [currentChatId, chatHistories]
  )

  const editUserMessage = useCallback(
    async (messageId: string, newContent: string) => {
      const chatId = currentChatId
      if (!chatId) throw new Error('No active chat')
      if (errorType === 'offline') throw new Error('You appear to be offline')

      const trimmed = newContent.trim()
      if (!trimmed) throw new Error('Message is empty')

      const chat = chatHistoriesRef.current.find((c) => c.id === chatId)
      if (!chat) throw new Error('Chat not found')

      const originalSnapshot = chat.messages.map((m) => ({ ...m }))
      const sorted = [...chat.messages].sort(sortMessagesChronological)
      const editIdx = sorted.findIndex((m) => m.id === messageId)
      if (editIdx === -1) throw new Error('Message not found')

      const before = sorted.slice(0, editIdx)
      const newUserMessage: Message = {
        id: Date.now().toString(),
        content: trimmed,
        sender: 'user',
        timestamp: new Date(),
      }
      const streamingPartial: Message = {
        id: 'streaming',
        content: '',
        sender: 'aira',
        timestamp: new Date(),
      }

      setChatHistories((prev) =>
        prev.map((c) => {
          if (c.id !== chatId) return c
          return {
            ...c,
            messages: [...before, newUserMessage, streamingPartial],
            timestamp: new Date(),
          }
        })
      )
      setTimeout(() => scrollToBottom(), 0)
      setIsTyping(true)
      setIsEditingMessage(true)
      setErrorType(null)

      try {
        const formData = new FormData()
        formData.append('message_id', messageId)
        formData.append('new_content', trimmed)
        formData.append('chat_id', chatId)
        if (chat.activeSkillId) formData.append('skill_id', chat.activeSkillId)

        const res = await fetch(`${API_BASE_URL}chat/edit-message`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        })
        logEffectiveChatModelFromResponse(res, 'edit')

        if (!res.ok) {
          let errMsg = 'Edit failed'
          try {
            const errBody = (await res.json()) as { error?: string }
            if (errBody?.error) errMsg = errBody.error
          } catch {
            // ignore
          }
          throw new Error(errMsg)
        }

        if (!res.body) throw new Error('No response body')
        const reader = res.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let botResp = ''
        let editTruncatedMaxTokens = false
        let sseBuffer = ''
        const processEditPiece = (piece: string) => {
          if (!piece) return
          const parsed = parseChatStreamSsePiece(piece)
          if (parsed.kind === 'title_update') {
            setChatHistories((prev) =>
              prev.map((c) => (c.id === parsed.chat_id ? { ...c, title: parsed.title } : c))
            )
            return
          }
          if (parsed.kind === 'assistant_truncated') {
            editTruncatedMaxTokens = true
            return
          }
          if (parsed.kind === 'control') return
          botResp += parsed.text
          const partial: Message = {
            id: 'streaming',
            content: botResp,
            sender: 'aira',
            timestamp: new Date(),
          }
          setChatHistories((prev) =>
            prev.map((c) => {
              if (c.id !== chatId) return c
              const without = c.messages.filter((m) => m.id !== 'streaming')
              return { ...c, messages: [...without, partial] }
            })
          )
        }
        while (true) {
          const { value, done } = await reader.read()
          const chunk = value ? decoder.decode(value, { stream: !done }) : ''
          const drained = drainChatSseDataPieces(sseBuffer, chunk)
          sseBuffer = drained.rest
          for (const piece of drained.pieces) {
            processEditPiece(piece)
          }
          if (done) {
            const tail = trailingChatSseDataPiece(sseBuffer)
            if (tail) processEditPiece(tail)
            break
          }
        }

        const editFinalContent = botResp
        const final: Message = {
          id: (Date.now() + 1).toString(),
          content: editFinalContent,
          sender: 'aira',
          timestamp: new Date(),
          ...(editTruncatedMaxTokens ? { truncatedMaxTokens: true } : {}),
        }
        setChatHistories((prev) =>
          prev.map((c) => {
            if (c.id !== chatId) return c
            const without = c.messages.filter((m) => m.id !== 'streaming')
            return { ...c, messages: [...without, final], timestamp: new Date() }
          })
        )

        try {
          if (editTruncatedMaxTokens) {
            await new Promise((r) => setTimeout(r, 150))
          }
          const historyRes = await fetch(`${API_BASE_URL}chat/stream-history/${chatId}`, {
            credentials: 'include',
          })
          if (historyRes.ok) {
            const historyData = await historyRes.json()
            const history: ApiHistoryRow[] = historyData.history || []
            setChatHistories((prev) =>
              prev.map((c) => {
                if (c.id !== chatId) return c
                let mapped = history.map(mapApiHistoryRowToMessage)
                if (editTruncatedMaxTokens) {
                  mapped = mergeHistoryWithTruncatedAssistantIfPending(mapped, final, editTruncatedMaxTokens)
                }
                mapped = mergeTruncationIntoLastAssistant(
                  mapped,
                  editFinalContent,
                  editTruncatedMaxTokens
                )
                mapped = mergeSkillOffersIntoHistory(mapped, c.messages)
                return {
                  ...c,
                  messages: mapped,
                  timestamp: new Date(),
                }
              })
            )
          }
        } catch {
          // keep streamed final
        }
      } catch (err) {
        setErrorType('ai')
        setChatHistories((prev) =>
          prev.map((c) => {
            if (c.id !== chatId) return c
            return { ...c, messages: originalSnapshot }
          })
        )
        throw err instanceof Error ? err : new Error('Edit failed')
      } finally {
        setIsTyping(false)
        setIsEditingMessage(false)
      }
    },
    [currentChatId, errorType, scrollToBottom]
  )

  const sidebarHistories: ChatHistoryItem[] = useMemo(
    () => chatHistories.map((c) => ({ id: c.id, title: c.title, timestamp: c.timestamp })),
    [chatHistories]
  )

  const currentChatTimestamp = useMemo(() => {
    const active = chatHistories.find((c) => c.id === currentChatId)
    return active?.timestamp ?? new Date()
  }, [chatHistories, currentChatId])

  /** When null, user is in a draft / welcome state — highlight "New conversation", not the first saved chat */
  const activeChatId = currentChatId ?? 0

  const lastAiMessageId =
    messages.length > 0
      ? [...messages].reverse().find((m) => m.sender === 'aira')?.id ?? null
      : null

  const lastUserMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') return messages[i]
    }
    return null
  }, [messages])

  const lastUserMessageId = lastUserMessage?.id ?? null

  const onEditUserMessageProp =
    currentChatId &&
    lastUserMessage &&
    !lastUserMessage.file_name &&
    !isTyping &&
    !regeneratingMessageId &&
    !isEditingMessage
      ? editUserMessage
      : undefined

  if (showFTU) {
    return (
      <FTUModal
        onClose={onFTUClose}
        onOpenTC={() => setShowTC(true)}
      />
    )
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: '100vh', overflow: 'hidden' }}>
      {showGuide && <GuideModal onClose={() => setShowGuide(false)} />}
      {showTC && <TCModal onClose={() => setShowTC(false)} />}
      <RenameModal
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        currentTitle={chatHistories.find((c) => c.id === chatToRename)?.title ?? ''}
        onConfirm={confirmRenameChat}
      />
      <ConfirmModal
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete this conversation?"
        description="All messages will be lost permanently."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDeleteChat}
      />
      {showProfile && (
        <ProfileModal
          onClose={() => setShowProfile(false)}
          onOpenGuide={() => {
            setShowProfile(false)
            setShowGuide(true)
          }}
          onOpenTC={() => {
            setShowProfile(false)
            setShowTC(true)
          }}
          userDisplayName={userDisplayName}
          userSubtitle="Quantum Leap · Achiever"
          userInitials={userInitials}
          isAdmin={isAdmin}
          onOpenAdmin={() => {
            setShowProfile(false)
            navigate('/admin')
          }}
        />
      )}
      <Sidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((o) => !o)}
        onClose={() => setSidebarOpen(false)}
        activeChat={activeChatId}
        chatTitle={chatTitle}
        onNewChat={handleNewChat}
        onLoadChat={handleLoadChat}
        onOpenGuide={() => setShowGuide(true)}
        onOpenProfile={() => setShowProfile(true)}
        onOpenTC={() => setShowTC(true)}
        hasHistory={hasHistory}
        chatHistories={sidebarHistories}
        currentChatTimestamp={currentChatTimestamp}
        userDisplayName={userDisplayName}
        userSubtitle="Quantum Leap · Achiever"
        userInitials={userInitials}
        onRenameChat={handleRenameChat}
        onDeleteChat={handleDeleteChat}
        hasMoreSessions={hasMoreSessions}
        loadingMoreSessions={loadingMoreSessions}
        onLoadMoreSessions={loadMoreSessions}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          minWidth: 0,
          marginLeft: sidebarOpen && !isMobile ? 'var(--sb-w)' : 0,
          transition: 'margin-left .3s cubic-bezier(.16,1,.3,1)',
        }}
      >
        <TopBar title={chatTitle} />
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!messages.some((m) => m.sender === 'user') && !isTyping ? (
            <WelcomeScreen
              onSend={(t, files, fileToUpload) => sendMessage(t, files, fileToUpload)}
              onInvokeSkill={(msg, skillId) => {
                void startSkillOffer(msg, skillId)
              }}
              onOpenGuide={() => setShowGuide(true)}
              userName={userFirstName}
            />
          ) : (
            <MessageList
              messages={refMessages}
              isTyping={isTyping}
              userInitials={userInitials}
              playingMessageId={playingMessageId}
              isPaused={isPaused}
              onPlayMessage={playMessage}
              onStopPlayback={stopPlayback}
              onRegenerate={regenerateMessage}
              lastAiMessageId={lastAiMessageId}
              lastUserMessageId={lastUserMessageId}
              onEditUserMessage={onEditUserMessageProp}
              regeneratingMessageId={regeneratingMessageId}
              onContinueAssistant={() => {
                void sendMessage('continue')
              }}
              onSkillOfferAccurate={onSkillOfferAccurate}
              onSkillOfferQuick={onSkillOfferQuick}
            />
          )}
        </div>
        {(messages.some((m) => m.sender === 'user') || isTyping) && (
          <InputBar
            onSend={(text, files, fileToUpload) => sendMessage(text, files, fileToUpload)}
            onInvokeSkill={(msg, skillId) => {
              void startSkillOffer(msg, skillId)
            }}
          />
        )}
      </div>
      <FloatingAudioControl
        isPlaying={!!playingMessageId}
        isPaused={isPaused}
        onPlayPause={togglePlayPause}
        onStop={stopPlayback}
      />
      <Toaster position="top-center" richColors closeButton />
    </div>
  )
}
